/**
 * AuditService — records immutable audit trail entries.
 *
 * Every privileged action is logged with: actor, target,
 * timestamp, previous value, new value, and action type.
 * Audit records are persisted to the audit_log table via
 * the Supabase client. The service is provider-agnostic —
 * swap the storage backend by implementing AuditStorage.
 */

import { getSupabase } from '@/lib/supabase';
import { LoggerFactory } from '@/lib/logging';

export type AuditAction =
  | 'profile_update'
  | 'application_submit'
  | 'status_change'
  | 'review_opened'
  | 'role_update'
  | 'permission_change'
  | 'admin_access';

export interface AuditRecord {
  actor_id: string;
  actor_role: string;
  target_type: string;
  target_id: string;
  action: AuditAction;
  previous_value: unknown;
  new_value: unknown;
  notes?: string;
}

export interface AuditStorage {
  store(record: AuditRecord & { timestamp: string }): Promise<void>;
}

class SupabaseAuditStorage implements AuditStorage {
  async store(record: AuditRecord & { timestamp: string }): Promise<void> {
    try {
      const supabase = getSupabase();
      // The actor identity and role are derived server-side from the caller's
      // session; anything the client claims about who acted is ignored.
      const { error } = await supabase.rpc('log_audit_event', {
        p_target_type: record.target_type,
        p_target_id: record.target_id,
        p_action: record.action,
        p_previous_value: record.previous_value ?? null,
        p_new_value: record.new_value ?? null,
        p_notes: record.notes ?? null,
      });
      if (error) {
        LoggerFactory.create().error('Failed to store audit record', { error: error.message });
      }
    } catch (err) {
      LoggerFactory.create().error('Audit storage exception', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
}

class AuditServiceImpl {
  private storage: AuditStorage;
  private logger = LoggerFactory.create();

  constructor(storage?: AuditStorage) {
    this.storage = storage ?? new SupabaseAuditStorage();
  }

  setStorage(storage: AuditStorage): void {
    this.storage = storage;
  }

  async record(record: AuditRecord): Promise<void> {
    const timestamp = new Date().toISOString();
    this.logger.audit(`[${record.action}] ${record.target_type}:${record.target_id}`, {
      actorId: record.actor_id,
      actorRole: record.actor_role,
      targetType: record.target_type,
      targetId: record.target_id,
      action: record.action,
    });
    await this.storage.store({ ...record, timestamp });
  }
}

export const auditService = new AuditServiceImpl();
